
import React from 'react';
import { useParams } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import TerraceDetailHeader from '@/components/terrace-detail/TerraceDetailHeader';
import TerraceImageSection from '@/components/terrace-detail/TerraceImageSection';
import TerraceInfoSection from '@/components/terrace-detail/TerraceInfoSection';
import TerraceFeatureTags from '@/components/terrace-detail/TerraceFeatureTags';
import SunForecastCard from '@/components/terrace-detail/SunForecastCard';
import TerraceActionButtons from '@/components/terrace-detail/TerraceActionButtons';
import TerraceMapSection from '@/components/terrace-detail/TerraceMapSection';
import { VisitorFeedbackSection } from '@/components/visitor-feedback/VisitorFeedbackSection';
import Footer from '@/components/Footer';
import { useTerraceDetail } from '@/hooks/useTerraceDetail';

const TerraceDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { 
    terrace, 
    loading, 
    error, 
    isSunStatusUpdating,
    triggerSunStatusUpdate,
    canAutoUpdate,
    updateConditions
  } = useTerraceDetail(slug);

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-warm-cream">
        <NavBar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-48 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-32 mx-auto"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !terrace) {
    return (
      <div className="flex flex-col min-h-screen bg-warm-cream">
        <NavBar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Terrace not found</h1>
            <p className="text-muted-foreground mb-4">
              {error || "The terrace you're looking for doesn't exist or may have been removed."}
            </p>
            <TerraceDetailHeader />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-warm-cream">
      <NavBar />
      
      <main className="flex-1">
        <div className="container px-3 sm:px-4 md:px-6 py-4 sm:py-6">
          <TerraceDetailHeader />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Left Column */}
            <div className="space-y-4 sm:space-y-6">
              <TerraceImageSection
                photoUrl={terrace.photo_url}
                name={terrace.name}
                sunStatus={terrace.sunStatus}
                isSunStatusUpdating={isSunStatusUpdating}
              />
              
              <TerraceMapSection terrace={terrace} />
            </div>
            
            {/* Right Column */}
            <div className="space-y-4 sm:space-y-6">
              <TerraceInfoSection
                name={terrace.name}
                address={terrace.address}
                openingHours={terrace.openingHours}
                detailedOpeningHours={terrace.detailed_opening_hours}
                terraceId={terrace.id}
              />
              
              <TerraceFeatureTags
                orientation={terrace.orientation}
                hasFood={terrace.hasFood}
                petFriendly={terrace.petFriendly}
                servesAlcohol={terrace.servesAlcohol}
              />
              
              {/* Wrap SunForecastCard in error boundary to prevent page refresh on errors */}
              <div key={`sun-forecast-${terrace.id}`}>
                <SunForecastCard
                  sunStatus={terrace.sunStatus}
                  lastUpdated={terrace.sun_status_last_updated}
                  isUpdating={isSunStatusUpdating}
                  onManualUpdate={triggerSunStatusUpdate}
                  canAutoUpdate={canAutoUpdate}
                  updateConditions={updateConditions}
                />
              </div>
              
              <TerraceActionButtons
                website={terrace.website}
                phone={terrace.phone}
              />
            </div>
          </div>
          
          <div className="mt-6 sm:mt-8">
            <VisitorFeedbackSection 
              terraceId={terrace.id}
              terraceName={terrace.name}
            />
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default TerraceDetail;
